import { useLocation, type Location } from 'react-router';
import { useSuspenseMaxJoinKeysQuery } from '#api/join-keys.queries.ts';
import { LinkButton } from '#components/atoms/buttons/link-button/LinkButton.tsx';
import { ButtonGroup, ButtonGroupItem } from '#components/patterns/button-group/ButtonGroup.tsx';
import { Snippet } from '#components/patterns/code/snippet/Snippet.tsx';
import { TableLayout } from '#components/patterns/layouts/table/TableLayout.tsx';
import { PageHeader } from '#components/patterns/page-header/PageHeader.tsx';
import type { AppState } from '#hooks/useAppLocation.ts';
import type { NewKeyState } from '#screens/settings/api-keys/types.ts';
import { EmptyJoinKeys } from '../list/EmptyJoinKeys.tsx';
import { JoinKeysList } from '../list/JoinKeysList.tsx';

type LocationState =
  | (AppState & {
      newKey?: NewKeyState;
    })
  | undefined;

export function JoinKeysPage() {
  const location = useLocation() as Location<LocationState>;
  const {
    data: { data: joinKeys },
    isFetching,
  } = useSuspenseMaxJoinKeysQuery();
  const newKey = location.state?.newKey;

  if (!joinKeys.length) {
    return <EmptyJoinKeys addJoinKeyHref="add" />;
  }

  return (
    <TableLayout
      headerClass="padb-0"
      header={
        <PageHeader
          className="mb-32"
          title="Host join keys"
          subtitle={
            newKey ?
              <>
                <p className="label">
                  Join key <strong>{newKey.name}</strong> created. Copy and store it somewhere safe. It cannot be viewed
                  after leaving this page. Enroll machines with it using <code>dnclient enroll -code</code>.
                </p>
                <Snippet variant="success" breakWord={true}>
                  {newKey.token}
                </Snippet>
              </>
            : null
          }
        >
          <ButtonGroup>
            <ButtonGroupItem>
              <LinkButton size="small" to="add">
                Add
              </LinkButton>
            </ButtonGroupItem>
          </ButtonGroup>
        </PageHeader>
      }
    >
      <JoinKeysList className="mb-48" data={joinKeys} isFetching={isFetching} />
    </TableLayout>
  );
}
